import {
    Music,
} from "lucide-react";
import {
    IconCorrectional,
    IconMunicipal,
    IconNightlife,
    IconNonprofit,
    IconHealthcare,
    IconSchool,
} from "../visuals/ServeIcons";
import { SectionNumber } from "../visuals/SectionNumber";

const cohorts = [
    {
        icon: IconCorrectional,
        title: "Correctional Facilities",
        desc: "Provide rapid, audit-ready access to over-the-counter Naloxone across cell blocks, intake, and release areas.",
    },
    {
        icon: IconMunicipal,
        title: "Municipalities",
        desc: "Deploy at transit hubs, libraries, parks, and government buildings — measurable, fundable public-wellness infrastructure.",
    },
    {
        icon: IconNightlife,
        title: "Nightlife & Venues",
        desc: "Clubs, festivals, bars, and concert venues — high-traffic environments that benefit from ready access to OTC emergency supplies. Discreet placement, anonymous access.",
    },
    {
        icon: IconNonprofit,
        title: "Nonprofits",
        desc: "Plug-and-play units for harm-reduction and community-health programs with audit-ready reporting and zero technical overhead.",
    },
    {
        icon: IconHealthcare,
        title: "Healthcare Systems",
        desc: "Extend OTC Naloxone access beyond the ER — into clinics, lobbies, and community sites.",
    },
    {
        icon: IconSchool,
        title: "Schools & Universities",
        desc: "Discreet, education-aligned units paired with training resources for resident advisors and staff.",
    },
];

export const WhoWeServe = () => {
    return (
        <section
            id="who"
            data-testid="who-we-serve-section"
            className="relative bg-white py-28 md:py-36"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="reveal flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
                    <div className="max-w-2xl">
                        <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            Who We Serve
                        </span>
                        <h2 className="mt-6 font-display font-extrabold text-4xl md:text-6xl leading-[0.98] tracking-tighter text-[#0A1428] text-balance">
                            Built for the places
                            <br />
                            where minutes matter.
                        </h2>
                    </div>
                    <p className="max-w-md text-base md:text-lg text-slate-600 font-medium leading-relaxed">
                        Kairos is built for operators and communities focused
                        on wellness readiness — deploying where reliable
                        access to OTC emergency supplies is needed most.
                    </p>
                </div>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                    {cohorts.map((c, i) => (
                        <div
                            key={c.title}
                            data-testid={`serve-card-${i}`}
                            className="reveal group relative bg-white p-8 md:p-10 transition-colors duration-300 hover:bg-[#F0493E] cursor-default text-[#0A1428] hover:text-white"
                        >
                            <c.icon size={32} />
                            <h3 className="mt-8 font-display font-bold text-2xl text-[#0A1428] group-hover:text-white transition-colors">
                                {c.title}
                            </h3>
                            <p className="mt-3 text-sm text-slate-600 group-hover:text-white/90 transition-colors leading-relaxed font-medium">
                                {c.desc}
                            </p>
                            <div className="mt-8 h-px w-10 bg-[#0A1428] group-hover:bg-white transition-colors" />
                        </div>
                    ))}
                </div>

                <div className="reveal mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="text-xs font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                        Don&apos;t see your sector?
                    </div>
                    <button
                        data-testid="serve-filler-cta"
                        onClick={() => {
                            window.dispatchEvent(
                                new CustomEvent("kairos:set-inquiry", {
                                    detail: "general",
                                })
                            );
                            document
                                .getElementById("contact")
                                ?.scrollIntoView({ behavior: "smooth" });
                        }}
                        className="link-underline font-display font-bold text-xl text-[#0A1428]"
                    >
                        Tell us where →
                    </button>
                </div>
            </div>
        </section>
    );
};
